import { ProjectType } from '@/api/common-types'
import type { WorkItem, WorkItemPipelineStage, WorkItemStatus } from '@/modules/project/api/work-item'
import { normalizePipelineStageForDisplay } from '@/modules/project/api/work-item'
import type { Router } from 'vue-router'

/** 从项目详情「查看」进入对应工作区（优先 pipeline_stage） */
export function getWorkItemWorkspacePath(item: WorkItem): string {
  const id = item.id
  const isLesson = item.item_type === ProjectType.LESSON
  const isVideo = item.item_type === ProjectType.VIDEO
  const stage = (item.pipeline_stage || '') as WorkItemPipelineStage | string
  const status: WorkItemStatus = item.status
  const displayStage = normalizePipelineStageForDisplay(item.item_type, String(stage))

  if (isLesson) {
    // designing → 设计；其余制品态（含 notes_ready / 活跃委托）→ 讲稿，便于多语种再指派
    if (displayStage === 'designing' || stage === 'designing') {
      return `/teaching/lesson/design/${id}`
    }
    if (
      displayStage === 'completed' ||
      status === 'completed' ||
      displayStage === 'notes_ready' ||
      displayStage === 'failed' ||
      stage === 'machine_running' ||
      stage === 'awaiting_review' ||
      stage === 'in_review'
    ) {
      return `/teaching/lesson/lecture-notes/${id}`
    }
    return `/teaching/lesson/design/${id}`
  }

  if (isVideo) {
    if (stage === 'completed' || status === 'completed') {
      return `/video/results/${id}`
    }
    if (
      displayStage === 'workspace_ready' ||
      stage === 'in_review' ||
      stage === 'awaiting_review'
    ) {
      return `/video/translation-review/${id}`
    }
    if (
      stage === 'uploaded' ||
      stage === 'machine_running' ||
      stage === 'subtitles_ready' ||
      stage === 'failed'
    ) {
      return `/video/subtitle-edit/${id}`
    }
    if (status === 'submitted' || status === 'processing') {
      return `/video/translation-review/${id}`
    }
    return `/video/subtitle-edit/${id}`
  }

  if (stage === 'completed' || status === 'completed') {
    return `/doc-workflow/results/${id}`
  }
  return `/doc-workflow/translation/${id}`
}

/** 任务中心 accept 后跳转工作区；失败时由调用方 Toast */
export async function openWorkItemWorkspace(
  router: Router,
  item: WorkItem,
): Promise<void> {
  const path = getWorkItemWorkspacePath(item)
  await router.push(path)
}

export function workItemStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'orange',
    processing: 'blue',
    submitted: 'purple',
    completed: 'green',
    rejected_at_assignment: 'red',
    no_task: 'default',
    uploaded: 'default',
    machine_running: 'blue',
    subtitles_ready: 'geekblue',
    workspace_ready: 'cyan',
    awaiting_review: 'cyan',
    in_review: 'cyan',
    failed: 'red',
    designing: 'gold',
    notes_ready: 'cyan',
  }
  return colors[status] || 'default'
}

export function pipelineStageLabelKey(stage: string): string {
  const map: Record<string, string> = {
    uploaded: 'stageUploaded',
    designing: 'stageDesigning',
    notes_ready: 'stageNotesReady',
    machine_running: 'stageMachineRunning',
    subtitles_ready: 'stageSubtitlesReady',
    workspace_ready: 'stageWorkspaceReady',
    // Legacy delegation stages: display as ready copy (caller should normalize by item_type)
    awaiting_review: 'stageWorkspaceReady',
    in_review: 'stageWorkspaceReady',
    completed: 'stageCompleted',
    failed: 'stageFailed',
  }
  return map[stage] || 'statusUnknown'
}

/** 目录状态主标签：制品 stage + 历史委托态读映射 */
export function displayPipelineStage(item: WorkItem): string {
  return normalizePipelineStageForDisplay(
    item.item_type,
    String(item.pipeline_stage || item.status || 'unknown'),
  )
}
